import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateWhitelist } from '../useWhitelistedProviders';
import { useWorkspaceWhitelistRules } from '../useWorkspaceWhitelistRules';
import { notificationService } from '@/lib/services/notification.service';
import type { ModelProviderWhitelistResponseDto } from '@inference.engine/api-client/web';

interface UseSaveProviderWhitelistParams {
  orgLevelProviders: ModelProviderWhitelistResponseDto[];
  selectedProviders: string[];
  allWhitelistedProviders: ModelProviderWhitelistResponseDto[];
  onSaveSuccess: () => void;
}

export const useSaveProviderWhitelist = ({
  orgLevelProviders,
  selectedProviders,
  allWhitelistedProviders,
  onSaveSuccess,
}: UseSaveProviderWhitelistParams) => {
  const { t } = useTranslation('admin');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [affectedProviders, setAffectedProviders] = useState<Array<{ key: string; name: string }>>(
    [],
  );

  const updateWhitelistMutation = useUpdateWhitelist();
  const workspaceRules = useWorkspaceWhitelistRules(allWhitelistedProviders);

  // Check which providers being removed are used in workspace rules
  const providersWithActiveRules = useMemo(() => {
    const currentOrgProviderKeys = orgLevelProviders.map((p) => p.key);
    const removedProviderKeys = currentOrgProviderKeys.filter(
      (key) => !selectedProviders.includes(key),
    );

    if (removedProviderKeys.length === 0) {
      return [];
    }

    // Find removed providers that are used in any workspace rules
    const affectedProviderKeys = new Set<string>();
    workspaceRules.forEach((rule) => {
      rule.providers.forEach((p) => {
        if (removedProviderKeys.includes(p.key)) {
          affectedProviderKeys.add(p.key);
        }
      });
    });

    // Map back to provider objects with key and name
    return orgLevelProviders
      .filter((p) => affectedProviderKeys.has(p.key))
      .map((p) => ({ key: p.key, name: p.name }));
  }, [orgLevelProviders, selectedProviders, workspaceRules]);

  // Perform the actual save
  const performSave = useCallback(async () => {
    try {
      await updateWhitelistMutation.mutateAsync({ providerIds: selectedProviders });
      notificationService.showSuccess(
        t('admin.useSaveProviderWhitelist.modelProviderWhitelistUpdated'),
      );
      onSaveSuccess();
      setShowConfirmation(false);
    } catch {
      notificationService.showError(
        t('admin.useSaveProviderWhitelist.failedToUpdateModelProvider'),
      );
    }
  }, [updateWhitelistMutation, selectedProviders, onSaveSuccess, t]);

  // Handle save click - show confirmation if needed, otherwise save directly
  const handleSave = useCallback(async () => {
    if (providersWithActiveRules.length > 0) {
      setAffectedProviders(providersWithActiveRules);
      setShowConfirmation(true);
    } else {
      await performSave();
    }
  }, [providersWithActiveRules, performSave]);

  const handleConfirmRemove = useCallback(() => {
    void performSave();
  }, [performSave]);

  const handleCancelConfirmation = useCallback(() => {
    setShowConfirmation(false);
    setAffectedProviders([]);
  }, []);

  return {
    showConfirmation,
    affectedProviders,
    isProcessing: updateWhitelistMutation.isPending,
    handleSave,
    handleConfirmRemove,
    handleCancelConfirmation,
  };
};
